import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useBranches } from '@/hooks/api/branches';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Search, Plus, Users, UserCheck, UserX, Filter, Edit2, Trash2, Eye } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { usePagination } from '@/hooks/use-pagination';
import { PaginationControls } from '@/components/PaginationControls';
import { ExportButtons } from '@/components/ExportButtons';
import { BranchFilter } from '@/components/BranchFilter';

type CustomerStatus = 'lead' | 'active' | 'blocked';
type CustomerSource = 'pos_sale' | 'murabaha_application' | 'manual' | 'b2b';

interface Customer {
  id: number;
  full_name: string;
  phone: string;
  passport: string | null;
  pinfl: string | null;
  status: CustomerStatus;
  source: CustomerSource;
  total_purchases: number;
  total_spent: string;
  murabaha_limit: string | null;
  notes: string | null;
  created_at: string | null;
  branch_id: number | null;
}

const STATUS_MAP: Record<CustomerStatus, { labelKey: string; className: string }> = {
  lead: { labelKey: 'cust_list.status_lead', className: 'bg-warning/10 text-warning border-warning/20' },
  active: { labelKey: 'cust_list.status_active', className: 'bg-success/10 text-success border-success/20' },
  blocked: { labelKey: 'cust_list.status_blocked', className: 'bg-destructive/10 text-destructive border-destructive/20' },
};

const SOURCE_MAP: Record<CustomerSource, string> = {
  pos_sale: 'cust_common.source_pos_sale',
  murabaha_application: 'cust_common.source_murabaha',
  manual: 'cust_common.source_manual',
  b2b: 'cust_common.source_b2b',
};

function formatUZS(amount: number) {
  const locale = ({ uz: 'uz-UZ', ru: 'ru-RU', en: 'en-US' } as Record<string, string>)[i18n.language] ?? 'uz-UZ';
  return new Intl.NumberFormat(locale).format(amount) + ' ' + i18n.t('cust_common.currency');
}

export default function Customers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [branchFilter, setBranchFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [form, setForm] = useState({
    full_name: '', phone: '', passport: '', pinfl: '', source: 'manual' as CustomerSource, notes: '',
  });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: branches = [] } = useBranches();

  const { data: customers = [], isLoading } = useQuery({
    // Own key: Dashboard/Reports/Tickets cache the raw `{data, meta}` envelope under ['/customers']
    // (default queryFn) — sharing it crashed this page with «filter is not a function» (Sentry HISOBPOS-WEB-DEV-B).
    // Still under the '/customers' prefix, so every invalidateQueries(['/customers']) refreshes it.
    queryKey: ['/customers', 'list'],
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/customers');
      if (error) throw error;
      return (data?.data ?? []) as Customer[];
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await apiClient.POST('/customers', {
        body: {
          full_name: form.full_name,
          phone: form.phone,
          passport: form.passport || null,
          pinfl: form.pinfl || null,
          source: form.source,
          notes: form.notes || null,
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/customers'] });
      closeDialog();
      toast({ title: t('cust_list.created') });
    },
    onError: (err: any) => {
      toast({ title: t('cust_common.error'), description: err.message, variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!editingCustomer) return;
      const { data, error } = await apiClient.PATCH('/customers/{customer}', {
        params: { path: { customer: editingCustomer.id } },
        body: {
          full_name: form.full_name,
          phone: form.phone,
          passport: form.passport || null,
          pinfl: form.pinfl || null,
          source: form.source,
          notes: form.notes || null,
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/customers'] });
      closeDialog();
      toast({ title: t('cust_list.updated') });
    },
    onError: (err: any) => {
      toast({ title: t('cust_common.error'), description: err.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const { error } = await apiClient.DELETE('/customers/{customer}', {
        params: { path: { customer: id } },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/customers'] });
      setDeleteId(null);
      toast({ title: t('cust_list.deleted') });
    },
    onError: (err: any) => {
      toast({ title: t('cust_common.error'), description: err.message, variant: 'destructive' });
    },
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: CustomerStatus }) => {
      const { error } = await apiClient.PATCH('/customers/{customer}/status', {
        params: { path: { customer: id } },
        body: { status },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/customers'] });
      toast({ title: t('cust_list.status_updated') });
    },
    onError: (err: any) => {
      toast({ title: t('cust_common.error'), description: err.message, variant: 'destructive' });
    },
  });

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingCustomer(null);
    setForm({ full_name: '', phone: '', passport: '', pinfl: '', source: 'manual', notes: '' });
  };

  const openEdit = (c: Customer) => {
    setEditingCustomer(c);
    setForm({
      full_name: c.full_name,
      phone: c.phone,
      passport: c.passport || '',
      pinfl: c.pinfl || '',
      source: c.source,
      notes: c.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (editingCustomer) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  const branchName = (branchId: number | null) => branches.find((b: any) => b.id === branchId)?.name ?? null;

  const filtered = customers.filter(c => {
    const matchesSearch =
      c.full_name.toLowerCase().includes(search.toLowerCase()) ||
      c.phone.includes(search) ||
      (c.passport && c.passport.includes(search));
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    const matchesBranch = branchFilter === 'all' || String(c.branch_id) === branchFilter;
    return matchesSearch && matchesStatus && matchesBranch;
  });

  const pagination = usePagination(filtered, 25);

  const stats = {
    total: customers.length,
    active: customers.filter(c => c.status === 'active').length,
    leads: customers.filter(c => c.status === 'lead').length,
  };

  const customerExportData = filtered.map(c => ({
    ism: c.full_name, telefon: c.phone, passport: c.passport || '', filial: branchName(c.branch_id) || '',
    status: t(STATUS_MAP[c.status].labelKey), manba: t(SOURCE_MAP[c.source]),
    xaridlar: c.total_purchases, jami_summa: Number(c.total_spent),
    murabaha_limit: c.murabaha_limit ? Number(c.murabaha_limit) : '',
  }));
  const customerExportColumns = [
    { header: t('cust_list.col_name'), key: 'ism' }, { header: t('cust_common.phone'), key: 'telefon' },
    { header: t('cust_common.passport'), key: 'passport' }, { header: t('cust_common.branch'), key: 'filial' },
    { header: t('cust_common.status'), key: 'status' },
    { header: t('cust_common.source'), key: 'manba' }, { header: t('cust_list.col_purchases'), key: 'xaridlar' },
    { header: t('cust_list.col_total'), key: 'jami_summa' }, { header: t('cust_common.murabaha_limit'), key: 'murabaha_limit' },
  ];

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('cust_list.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('cust_list.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButtons data={customerExportData} columns={customerExportColumns} fileName="mijozlar" title={t('cust_list.export_title')} />
          <Button onClick={() => { setEditingCustomer(null); setForm({ full_name: '', phone: '', passport: '', pinfl: '', source: 'manual', notes: '' }); setDialogOpen(true); }} size="sm">
            <Plus size={16} className="mr-1" /> {t('cust_list.new_customer')}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg"><Users size={20} className="text-primary" /></div>
          <div>
            <p className="text-2xl font-bold text-foreground">{stats.total}</p>
            <p className="text-xs text-muted-foreground">{t('cust_list.stat_total')}</p>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
          <div className="p-2 bg-green-500/10 rounded-lg"><UserCheck size={20} className="text-green-600" /></div>
          <div>
            <p className="text-2xl font-bold text-foreground">{stats.active}</p>
            <p className="text-xs text-muted-foreground">{t('cust_list.stat_active')}</p>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
          <div className="p-2 bg-orange-500/10 rounded-lg"><UserX size={20} className="text-orange-600" /></div>
          <div>
            <p className="text-2xl font-bold text-foreground">{stats.leads}</p>
            <p className="text-xs text-muted-foreground">{t('cust_list.stat_leads')}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <Input placeholder={t('cust_list.search_placeholder')} value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <BranchFilter value={branchFilter} onChange={setBranchFilter} />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <Filter size={14} className="mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('cust_list.filter_all')}</SelectItem>
            <SelectItem value="lead">{t('cust_list.stat_leads')}</SelectItem>
            <SelectItem value="active">{t('cust_list.status_active')}</SelectItem>
            <SelectItem value="blocked">{t('cust_list.status_blocked')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cust_list.col_name')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cust_common.phone')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cust_common.passport')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cust_common.branch')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cust_common.status')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cust_common.source')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cust_list.col_purchases')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cust_list.col_total')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cust_common.murabaha_limit')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cust_list.col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">{t('cust_common.loading')}</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">{t('cust_list.not_found')}</td></tr>
              ) : (
                pagination.items.map(c => (
                  <tr key={c.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => navigate(`/customers/${c.id}`)}>
                    <td className="p-3 text-sm font-medium text-foreground">{c.full_name}</td>
                    <td className="p-3 text-sm text-muted-foreground">{c.phone}</td>
                    <td className="p-3 text-sm text-muted-foreground font-mono">{c.passport || '—'}</td>
                    <td className="p-3 text-sm text-muted-foreground whitespace-nowrap">{branchName(c.branch_id) || '—'}</td>
                    <td className="p-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium border ${STATUS_MAP[c.status].className}`}>
                        {t(STATUS_MAP[c.status].labelKey)}
                      </span>
                    </td>
                    <td className="p-3 text-sm text-muted-foreground">{t(SOURCE_MAP[c.source])}</td>
                    <td className="p-3 text-sm text-foreground">{c.total_purchases}</td>
                    <td className="p-3 text-sm text-foreground">{formatUZS(Number(c.total_spent))}</td>
                    <td className="p-3 text-sm text-foreground">
                      {c.murabaha_limit ? formatUZS(Number(c.murabaha_limit)) : '—'}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                        <button onClick={() => navigate(`/customers/${c.id}`)} className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground" title={t('cust_list.view')}>
                          <Eye size={14} />
                        </button>
                        <button onClick={() => openEdit(c)} className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground" title={t('cust_list.edit')}>
                          <Edit2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <PaginationControls page={pagination.page} totalPages={pagination.totalPages} total={pagination.total} pageSize={pagination.pageSize} onPrev={pagination.prevPage} onNext={pagination.nextPage} hasPrev={pagination.hasPrev} hasNext={pagination.hasNext} />
        </div>
      </div>

      {/* Add/Edit Customer Dialog */}
      <Dialog open={dialogOpen} onOpenChange={v => { if (!v) closeDialog(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCustomer ? t('cust_list.dialog_edit_title') : t('cust_list.dialog_new_title')}</DialogTitle>
            <DialogDescription>{t('cust_list.dialog_description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder={t('cust_list.ph_full_name')} value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} />
            <Input placeholder={t('cust_list.ph_phone')} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
            <Input placeholder={t('cust_list.ph_passport')} value={form.passport} onChange={e => setForm(f => ({ ...f, passport: e.target.value }))} />
            <Input placeholder={t('cust_list.ph_pinfl')} maxLength={14} value={form.pinfl} onChange={e => setForm(f => ({ ...f, pinfl: e.target.value }))} />
            <Select value={form.source} onValueChange={(v: CustomerSource) => setForm(f => ({ ...f, source: v }))}>
              <SelectTrigger><SelectValue placeholder={t('cust_common.source')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">{t('cust_common.source_manual')}</SelectItem>
                <SelectItem value="pos_sale">{t('cust_common.source_pos_sale')}</SelectItem>
                <SelectItem value="murabaha_application">{t('cust_common.source_murabaha')}</SelectItem>
                <SelectItem value="b2b">{t('cust_common.source_b2b')}</SelectItem>
              </SelectContent>
            </Select>
            <Textarea placeholder={t('cust_list.ph_notes')} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>{t('cust_common.cancel')}</Button>
            <Button onClick={handleSave} disabled={!form.full_name || !form.phone || isSaving}>
              {isSaving ? t('cust_common.saving') : t('cust_list.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('cust_list.delete_title')}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t('cust_list.delete_confirm')}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>{t('cust_common.cancel')}</Button>
            <Button variant="destructive" onClick={() => deleteId && deleteMutation.mutate(deleteId)} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? t('cust_list.deleting') : t('cust_list.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
